import {
  useAllMatchStatsWC26, useGoalSeconds, useWC26ScoringRules, useWC26UserTeams,
} from '../../queries';
import { Goal } from '../../types';
import { goalLatestSortKey, goalTotalSeconds } from '../../utils/scoringUtils';
import Spinner from '../Spinner/Spinner';
import FirstLastGoalCardWC26 from './FirstLastGoalCardWC26';
import './FirstLastGoalsWC26.css';

function FirstLastGoalsWC26() {
  const [matchStatsLoading, matchStats, matchStatsError] = useAllMatchStatsWC26();
  const [rulesLoading, rules, rulesError] = useWC26ScoringRules();
  const [userTeamsLoading, userTeams, userTeamsError] = useWC26UserTeams();
  const [, goalSecondsData] = useGoalSeconds();

  const loading = matchStatsLoading || rulesLoading || userTeamsLoading;
  const error = matchStatsError || rulesError || userTeamsError;
  const secondsMap = goalSecondsData ?? {};

  const goals = (matchStats ?? [])
    .reduce<{ goal: Goal; match: string; matchId: number }[]>((acc, m) => {
      const homeTeam = m.statistics[0]?.name ?? '';
      const awayTeam = m.statistics[1]?.name ?? '';
      const match = `${homeTeam} vs ${awayTeam}`;
      return [...acc, ...m.goalEvents
        .filter((g) => g.type !== 'Own Goal' && g.type !== 'Missed Penalty')
        .map((goal) => ({ goal, match, matchId: m.id }))];
    }, []);

  const sortedEarliest = [...goals].sort(
    (a, b) => goalTotalSeconds(a.matchId, a.goal, secondsMap)
      - goalTotalSeconds(b.matchId, b.goal, secondsMap)
  );
  const topEarliestKey = sortedEarliest.length > 0
    ? goalTotalSeconds(sortedEarliest[0].matchId, sortedEarliest[0].goal, secondsMap) : 0;
  const tiedEarliest = sortedEarliest.filter(
    (g) => goalTotalSeconds(g.matchId, g.goal, secondsMap) === topEarliestKey
  );
  const earliestList = tiedEarliest.length >= 3 ? tiedEarliest : sortedEarliest.slice(0, 3);

  const sortedLatest = [...goals].sort(
    (a, b) => goalLatestSortKey(b.goal) - goalLatestSortKey(a.goal)
  );
  const topLatestKey = sortedLatest.length > 0 ? goalLatestSortKey(sortedLatest[0].goal) : 0;
  const tiedLatest = sortedLatest.filter((g) => goalLatestSortKey(g.goal) === topLatestKey);
  const latestList = tiedLatest.length >= 3 ? tiedLatest : sortedLatest.slice(0, 3);

  return (
    <div className="first-last-goals-wc26">
      <h2 className="first-last-goals-wc26__title">Quickest &amp; Latest Goals</h2>
      {error ? <p className="first-last-goals-wc26-empty">Failed to load data.</p>
        : loading ? <Spinner />
          : earliestList.length === 0 || !rules || !userTeams
            ? <p className="first-last-goals-wc26-empty">No goals yet</p>
            : (
              <>
                {earliestList.map((item, i) => (
                  <FirstLastGoalCardWC26
                    key={`earliest-${item.goal.playerName}-${item.goal.time}-${item.goal.extra}`}
                    goal={item.goal}
                    match={item.match}
                    matchId={item.matchId}
                    scoringRules={rules}
                    teamAllocs={userTeams}
                    title={i === 0 ? 'Quickest Goal' : ''}
                    points={i === 0 ? rules.fastestGoal : 0}
                    playerPhoto={item.goal.playerPhoto}
                    index={i}
                    secondsMap={secondsMap}
                  />
                ))}
                {latestList.map((item, i) => (
                  <FirstLastGoalCardWC26
                    key={`latest-${item.goal.playerName}-${item.goal.time}-${item.goal.extra}`}
                    goal={item.goal}
                    match={item.match}
                    matchId={item.matchId}
                    scoringRules={rules}
                    teamAllocs={userTeams}
                    title={i === 0 ? 'Latest Goal' : ''}
                    points={i === 0 ? rules.latestGoal : 0}
                    playerPhoto={item.goal.playerPhoto}
                    index={i}
                    secondsMap={secondsMap}
                  />
                ))}
              </>
            )}
    </div>
  );
}

export default FirstLastGoalsWC26;
